import type {
  CmmdAttachment,
  CmmdBoardTask,
  CmmdForgeTaskDetail,
  CmmdMember,
  CmmdProjectAgentMember,
  CmmdProjectTaskAssignmentSubject,
  CmmdProjectMilestone,
  CmmdTaskComment,
  CmmdTaskConversation,
  CmmdTaskConversationSummary,
  CmmdTaskHistory,
  CmmdWorkSession,
  CmmdTaskStatus,
  CmmdTaskWorkflow,
  CreateCmmdBoardTaskParams,
  UpdateCmmdBoardTaskParams,
} from "@cmmd-forge/contracts";
import type { ClipboardEvent, DragEvent } from "react";

import type { ResolvedTaskThreadHistoryGroup } from "~/lib/taskThreadLink";
import type { SidebarThreadSummary } from "~/types";
import { cn } from "~/lib/utils";
import { TaskAttachmentList } from "../command-center/TaskAttachmentList";
import { ProjectTaskActivity } from "./ProjectTaskActivity";
import { ProjectTaskDescription } from "./ProjectTaskDescription";
import { ProjectTaskHistory } from "./ProjectTaskHistory";
import { ProjectTaskForgeActivity } from "./ProjectTaskForgeActivity";
import { ProjectTaskDetailsRail } from "./ProjectTaskDetailsRail";
import { ProjectTaskDetailSection as Section } from "./ProjectTaskDetailSection";
import { ProjectTaskMobileTabs } from "./ProjectTaskMobileTabs";
import type { ProjectTaskMobileSection } from "./ProjectTaskMobileTabs";
import {
  ProjectTaskDependencies,
  ProjectTaskRelationships,
  ProjectTaskSubtasks,
} from "./ProjectTaskRelationships";
import { ProjectTaskWorkbenchHeader } from "./ProjectTaskWorkbenchHeader";

export interface ProjectTaskDetailContentProps {
  readonly task: CmmdBoardTask;
  readonly projectTitle: string;
  readonly projectIconUrl: string | null;
  readonly projectIcon: string | null;
  readonly allTasks: readonly CmmdBoardTask[];
  readonly members: readonly CmmdMember[];
  readonly agentMembers: readonly CmmdProjectAgentMember[];
  readonly agentMembersLoading: boolean;
  readonly agentMembersError: string | null;
  readonly milestones: readonly CmmdProjectMilestone[];
  readonly taskDetail: CmmdForgeTaskDetail | null;
  readonly attachments: readonly CmmdAttachment[];
  readonly comments: readonly CmmdTaskComment[];
  readonly conversations: readonly CmmdTaskConversationSummary[];
  readonly conversationsNextCursor: string | null;
  readonly selectedConversation: CmmdTaskConversation | null;
  readonly expandedConversationId: string | null;
  readonly conversationsLoaded: boolean;
  readonly conversationsLoading: boolean;
  readonly loadingMoreConversations: boolean;
  readonly loadingConversationId: string | null;
  readonly loadingMoreConversationEvents: boolean;
  readonly conversationsError: string | null;
  readonly conversationDetailError: string | null;
  readonly workSessions: readonly CmmdWorkSession[];
  readonly workSessionsLoaded: boolean;
  readonly workSessionsLoading: boolean;
  readonly workSessionsError: string | null;
  readonly taskHistory: CmmdTaskHistory | null;
  readonly taskHistoryLoading: boolean;
  readonly taskHistoryError: string | null;
  readonly activityLoading: boolean;
  readonly commenting: boolean;
  readonly activityError: string | null;
  readonly title: string;
  readonly description: string;
  readonly savedDescription: string;
  readonly pending: boolean;
  readonly statusMutationDisabled: boolean;
  /** Host cannot write; suppresses write affordances. Defaults to editable. */
  readonly readOnly?: boolean;
  readonly creating: boolean;
  readonly mutationError: string | null;
  readonly executing: boolean;
  readonly hasKnownConversation: boolean;
  readonly workflow: CmmdTaskWorkflow | null;
  readonly threadHistory: readonly ResolvedTaskThreadHistoryGroup[];
  readonly onTitleChange: (title: string) => void;
  readonly onDescriptionChange: (description: string) => void;
  readonly onCommitTitle: () => void;
  readonly onSaveDescription: (description: string) => Promise<boolean>;
  readonly onUpdate: (id: number, patch: UpdateCmmdBoardTaskParams) => Promise<boolean>;
  readonly onSetAssignment: (
    id: number,
    subject: CmmdProjectTaskAssignmentSubject,
  ) => Promise<boolean>;
  readonly onCreateTask: (params: CreateCmmdBoardTaskParams) => Promise<boolean>;
  readonly onCreateComment: (content: string) => Promise<void>;
  readonly onUpdateComment: (commentKey: string, content: string) => Promise<void>;
  readonly onDeleteComment: (commentKey: string) => Promise<void>;
  readonly onRetryActivity: () => void;
  readonly onEnsureConversationsLoaded: () => void;
  readonly onRetryConversations: () => void;
  readonly onLoadMoreConversations: () => void;
  readonly onSetStatus: (id: number, status: CmmdTaskStatus, workflowStageId?: string) => void;
  readonly onStartWork: () => void;
  readonly onLoadConversation: (conversationId: string) => void;
  readonly onLoadMoreConversationEvents: () => void;
  readonly onClearConversation: () => void;
  readonly onLoadTaskHistory: () => void;
  readonly onOpenThread: (thread: SidebarThreadSummary) => void;
  readonly onOpenTask: (id: number) => void;
  readonly onArchive: () => Promise<boolean>;
  readonly onUnarchive: () => void;
  readonly onPasteTaskImages: (event: ClipboardEvent<HTMLElement>) => void;
  readonly onDropTaskImages: (event: DragEvent<HTMLElement>) => void;
  readonly mobileSection: ProjectTaskMobileSection;
  readonly onMobileSectionChange: (section: ProjectTaskMobileSection) => void;
}

export function ProjectTaskDetailContent(props: ProjectTaskDetailContentProps) {
  const parentTask =
    props.task.parentTaskId === null
      ? null
      : (props.allTasks.find((task) => task.id === props.task.parentTaskId) ?? null);
  const relationshipProps = {
    task: props.task,
    allTasks: props.allTasks,
    pending: props.pending,
    creating: props.creating,
    mutationDisabled: props.statusMutationDisabled,
    workflow: props.workflow,
    onOpenTask: props.onOpenTask,
    onUpdate: props.onUpdate,
    onCreateTask: props.onCreateTask,
  } as const;
  const detailsRailProps = {
    task: props.task,
    taskDetail: props.taskDetail,
    allTasks: props.allTasks,
    members: props.members,
    agentMembers: props.agentMembers,
    agentMembersLoading: props.agentMembersLoading,
    agentMembersError: props.agentMembersError,
    milestones: props.milestones,
    pending: props.pending,
    statusMutationDisabled: props.statusMutationDisabled,
    workflow: props.workflow,
    onSetStatus: props.onSetStatus,
    onUpdate: props.onUpdate,
    onSetAssignment: props.onSetAssignment,
  } as const;
  const activityProps = {
    comments: props.comments,
    conversations: props.conversations,
    conversationsNextCursor: props.conversationsNextCursor,
    selectedConversation: props.selectedConversation,
    expandedConversationId: props.expandedConversationId,
    conversationsLoaded: props.conversationsLoaded,
    conversationsLoading: props.conversationsLoading,
    loadingMoreConversations: props.loadingMoreConversations,
    loadingConversationId: props.loadingConversationId,
    loadingMoreConversationEvents: props.loadingMoreConversationEvents,
    conversationsError: props.conversationsError,
    conversationDetailError: props.conversationDetailError,
    threadHistory: props.threadHistory,
    loading: props.activityLoading,
    commenting: props.commenting,
    error: props.activityError,
    onRetry: props.onRetryActivity,
    onEnsureConversationsLoaded: props.onEnsureConversationsLoaded,
    onRetryConversations: props.onRetryConversations,
    onLoadMoreConversations: props.onLoadMoreConversations,
    onCreateComment: props.onCreateComment,
    onUpdateComment: props.onUpdateComment,
    onDeleteComment: props.onDeleteComment,
    onStartWork: props.onStartWork,
    onLoadConversation: props.onLoadConversation,
    onLoadMoreConversationEvents: props.onLoadMoreConversationEvents,
    onClearConversation: props.onClearConversation,
    onOpenThread: props.onOpenThread,
  } as const;
  const descriptionSection = (
    <Section label="Description">
      <ProjectTaskDescription
        projectId={props.task.projectId}
        description={props.savedDescription}
        draft={props.description}
        pending={props.pending}
        onDraftChange={props.onDescriptionChange}
        onSave={props.onSaveDescription}
        readOnly={props.readOnly ?? false}
      />
    </Section>
  );
  const taskHistory = (
    <ProjectTaskHistory
      history={props.taskHistory}
      loading={props.taskHistoryLoading}
      error={props.taskHistoryError}
      onLoad={props.onLoadTaskHistory}
      workSessions={props.workSessions}
      workSessionsLoaded={props.workSessionsLoaded}
      workSessionsLoading={props.workSessionsLoading}
      workSessionsError={props.workSessionsError}
      conversationHistory={
        <ProjectTaskForgeActivity
          history={props.threadHistory}
          conversations={props.conversations}
          conversationsNextCursor={props.conversationsNextCursor}
          selectedConversation={props.selectedConversation}
          expandedConversationId={props.expandedConversationId}
          conversationsLoaded={props.conversationsLoaded}
          conversationsLoading={props.conversationsLoading}
          loadingMoreConversations={props.loadingMoreConversations}
          loadingConversationId={props.loadingConversationId}
          loadingMoreConversationEvents={props.loadingMoreConversationEvents}
          conversationsError={props.conversationsError}
          conversationDetailError={props.conversationDetailError}
          runs={[]}
          loading={false}
          onStartWork={props.onStartWork}
          onRetryConversations={props.onRetryConversations}
          onLoadMoreConversations={props.onLoadMoreConversations}
          onLoadConversation={props.onLoadConversation}
          onLoadMoreConversationEvents={props.onLoadMoreConversationEvents}
          onClearConversation={props.onClearConversation}
          onOpenThread={props.onOpenThread}
        />
      }
    />
  );
  const mobileDescriptionSection = (
    <div className="space-y-5">
      {descriptionSection}
      {props.attachments.length > 0 ? (
        <Section label="Attachments">
          <TaskAttachmentList attachments={props.attachments} />
        </Section>
      ) : null}
    </div>
  );

  return (
    <div
      className={cn("flex h-full min-h-0 flex-col", props.pending ? "opacity-80" : undefined)}
      onPaste={props.onPasteTaskImages}
      // dragOver must preventDefault or the browser keeps the default action
      // and navigates the window to the dropped file instead of attaching it.
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }}
      onDrop={props.onDropTaskImages}
    >
      <ProjectTaskWorkbenchHeader
        task={props.task}
        parentTask={parentTask}
        projectTitle={props.projectTitle}
        projectIconUrl={props.projectIconUrl}
        projectIcon={props.projectIcon}
        title={props.title}
        pending={props.pending}
        executing={props.executing}
        hasConversation={
          props.hasKnownConversation ||
          props.conversations.length > 0 ||
          props.threadHistory.length > 0
        }
        onTitleChange={props.onTitleChange}
        onCommitTitle={props.onCommitTitle}
        onOpenTask={props.onOpenTask}
        onStartWork={props.onStartWork}
        onArchive={props.onArchive}
        onUnarchive={props.onUnarchive}
        readOnly={props.readOnly ?? false}
      />
      {props.mutationError ? (
        <div
          role="alert"
          className="shrink-0 border-b border-destructive-border bg-destructive-surface px-4 py-2 text-sm text-destructive md:px-5"
        >
          {props.mutationError}
        </div>
      ) : null}
      <ProjectTaskMobileTabs
        taskId={props.task.id}
        section={props.mobileSection}
        onSectionChange={props.onMobileSectionChange}
        sections={{
          description: mobileDescriptionSection,
          subtasks: <ProjectTaskSubtasks {...relationshipProps} />,
          dependencies: <ProjectTaskDependencies {...relationshipProps} />,
          activity: <ProjectTaskActivity {...activityProps} />,
          history: taskHistory,
          details: <ProjectTaskDetailsRail variant="mobile" {...detailsRailProps} />,
        }}
      />
      <div
        data-task-workbench-scroll
        className="hidden min-h-0 flex-1 lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden"
      >
        <main
          data-task-workbench-main
          className="min-h-0 min-w-0 space-y-5 px-4 py-4 md:px-5 lg:overflow-y-auto lg:overscroll-y-contain lg:px-6"
        >
          {descriptionSection}
          {props.attachments.length > 0 ? (
            <Section label="Attachments">
              <TaskAttachmentList attachments={props.attachments} />
            </Section>
          ) : null}
          <ProjectTaskRelationships {...relationshipProps} />
          <ProjectTaskActivity {...activityProps} />
          {taskHistory}
        </main>
        <ProjectTaskDetailsRail variant="desktop" {...detailsRailProps} />
      </div>
    </div>
  );
}
